import React, { useState } from 'react';
import Icon from '@components/AppIcon';
import Image from '@components/AppImage';

const VirtualTour = ({ location, onClose }) => {
  const [currentView, setCurrentView] = useState(0);
  const [imageError, setImageError] = useState(false);

  // Use tour_views from location data or fallback to default
  const tourViews = location.tour_views && location.tour_views.length > 0 
    ? location.tour_views.map(view => ({
        id: view.id || Math.random(),
        name: view.area_name || 'Clinic Area',
        image: view.image && view.image.length > 0 
          ? view.image[0] // Use the first image from the array
          : 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=800&h=600&fit=crop',
        description: view.area_description || 'Modern clinic area designed for patient comfort and care.'
      }))
    : [
        {
          id: 1,
          name: "Reception Area",
          image: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=800&h=600&fit=crop",
          description: "Welcoming reception area with comfortable seating and friendly staff ready to assist you."
        },
        {
          id: 2,
          name: "Waiting Lounge",
          image: "https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?w=800&h=600&fit=crop",
          description: "Spacious waiting area with natural lighting and comfortable seating for patients and families."
        },
        {
          id: 3,
          name: "Examination Room",
          image: "https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=800&h=600&fit=crop",
          description: "State-of-the-art examination rooms equipped with the latest diagnostic technology."
        }
      ];

  const nextView = () => {
    setCurrentView((prev) => (prev + 1) % tourViews.length);
  };

  const prevView = () => {
    setCurrentView((prev) => (prev - 1 + tourViews.length) % tourViews.length);
  };

  const restartTour = () => {
    setCurrentView(0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
      <div className="relative w-full h-full max-w-6xl max-h-screen">
        {/* Header */}
        <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between">
          <div className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg">
            <h2 className="font-heading font-bold text-lg">
              Virtual Tour - {location.name}
            </h2>
            <p className="text-sm opacity-80">
              {tourViews[currentView].name} ({currentView + 1} of {tourViews.length})
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Main Image */}
        <div className="relative w-full h-full rounded-lg overflow-hidden">
          {!imageError ? (
            <Image
              src={tourViews[currentView].image}
              alt={tourViews[currentView].name}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#08005f] to-[#eb2227] flex items-center justify-center">
              <Icon name="Image" size={48} className="text-white opacity-80" />
            </div>
          )}
          
          {/* Navigation Arrows */}
          {tourViews.length > 1 && (
            <>
              <button
                onClick={prevView}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition"
              >
                <Icon name="ChevronLeft" size={24} />
              </button>
              <button
                onClick={nextView}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition"
              >
                <Icon name="ChevronRight" size={24} />
              </button>
            </>
          )}

          {/* Description Overlay */}
          <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 text-white p-4 rounded-lg">
            <h3 className="font-heading font-semibold text-lg mb-2">
              {tourViews[currentView].name}
            </h3>
            <p className="text-sm opacity-90">
              {tourViews[currentView].description}
            </p>
          </div>
        </div>

        {/* Thumbnail Navigation */}
        {tourViews.length > 1 && (
          <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex space-x-2 bg-black bg-opacity-50 p-2 rounded-lg">
            {tourViews.map((view, index) => (
              <button
                key={view.id}
                onClick={() => setCurrentView(index)}
                className={`w-16 h-12 rounded overflow-hidden border-2 transition ${
                  currentView === index ? 'border-[#eb2227]' : 'border-transparent opacity-60 hover:opacity-80'
                }`}
              >
                <Image
                  src={view.image}
                  alt={view.name}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}

        {/* Controls - Only Restart Tour Button */}
        <div className="absolute bottom-4 right-4">
          <button
            onClick={restartTour}
            className="flex items-center space-x-2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg hover:bg-opacity-70 transition"
          >
            <Icon name="RotateCcw" size={16} />
            <span>Restart Tour</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default VirtualTour;